/** Max snapshots kept before assistant apply-to-draft (each chat overwrite pushes one). */
export const MAX_CHAT_DRAFT_UNDO_STACK = 30;

/**
 * True when Lexical `EditorState.toJSON()` string has no visible text or substantive nodes.
 * Used so undo/redo clears the editor instead of feeding JSON through `clearEditor`, which can hit
 * parse failures and fall back to showing raw JSON as paragraph text.
 */
export function serializedLexicalJsonIsVisuallyEmpty(jsonStr: string): boolean {
  const trimmed = jsonStr.trim();
  if (!trimmed) return true;
  try {
    const data = JSON.parse(trimmed) as {root?: unknown};
    if (!data.root || typeof data.root !== 'object') return false;
    return !serializedLexicalNodeHasVisibleContent(data.root);
  } catch {
    return false;
  }
}

function serializedLexicalNodeHasVisibleContent(node: unknown): boolean {
  if (!node || typeof node !== 'object') return false;
  const n = node as Record<string, unknown>;
  const nodeType = n.type;

  if (nodeType === 'text' && typeof n.text === 'string' && n.text.trim().length > 0) {
    return true;
  }

  // Anything other than structural / empty leaf nodes counts as content (media, HR, tables, etc.).
  if (
    typeof nodeType === 'string' &&
    nodeType !== 'paragraph' &&
    nodeType !== 'root' &&
    nodeType !== 'linebreak'
  ) {
    return true;
  }

  if (Array.isArray(n.children)) {
    return n.children.some(serializedLexicalNodeHasVisibleContent);
  }

  return false;
}

/** Serialized editors + chrome state immediately before an assistant draft apply. */
export type ChatDraftSnapshot = {
  status: string;
  updateEditorJson: string;
  linkEditorJson: string | null;
};

/** Props for undo/redo UI when the assistant overwrites the status draft (not Lexical undo). */
export type ChatDraftHistoryControl = {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
};
